// src/components/onboarding/CompleteCollectionModal.tsx
// ✅ ENHANCED: Modal with detailed assignment feedback and pending categories display
// Implements the agreed-upon two-phase completion model with individual issue dates

import React, { useState } from 'react';
import { 
  CheckCircle2, 
  AlertTriangle, 
  Clock,
  ArrowRight,
  X,
  RefreshCw,
  Calendar,
  Package,
  User,
  AlertCircle,
  ExternalLink,
  CheckSquare
} from 'lucide-react';
import { OnboardingSession } from '../../types/onboarding';
import OnboardingAPI from '../../services/onboardingAPI';

interface CompleteCollectionModalProps {
  session: OnboardingSession;
  isOpen: boolean;
  onClose: () => void;
  onComplete: (updatedSession: OnboardingSession) => void;
}

interface CompletionResponse {
  message: string;
  session: OnboardingSession;
  summary: {
    totalItems: number;
    successful: number;
    failed: number;
    status: 'Complete' | 'Partial';
  };
  completedAssignments: Array<{
    itemId: string;
    itemName: string;
    assignmentId: string;
    status: 'Success';
  }>;
  failedAssignments: Array<{
    itemId: string;
    itemName: string;
    error: string;
    status: 'Failed';
  }>;
}

const CompleteCollectionModal: React.FC<CompleteCollectionModalProps> = ({
  session,
  isOpen,
  onClose,
  onComplete
}) => {
  const [completing, setCompleting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [completionData, setCompletionData] = useState<CompletionResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  if (!isOpen) return null;

  // Calculate current session state
  const reservedItems = session.reservedItems || [];
  const categories = session.categories || [];
  const readyCount = reservedItems.reduce((sum, item) => sum + (item.quantity || 1), 0);

  // Calculate pending categories with specific requirements
  const pendingCategories = categories
    .map(category => {
      const required = category.quantityRequired || 1;
      const selected = category.quantitySelected || 0;
      const stillNeeded = required - selected;
      
      return {
        categoryId: category.categoryId,
        categoryName: category.categoryName,
        quantityNeeded: stillNeeded,
        isComplete: stillNeeded <= 0
      };
    })
    .filter(cat => !cat.isComplete);

  const handleCompleteCollection = async () => {
    setCompleting(true);
    setError(null);

    try {
      console.log(`🎯 Completing collection for session: ${session.sessionId}`);
      
      const completionRequest = {
        notes: `Equipment collection completed on ${new Date().toLocaleDateString()}. ${readyCount} items assigned immediately.`
      };

      const response = await OnboardingAPI.completeSession(session.sessionId, completionRequest);
      
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Failed to complete session');
      }

      console.log('✅ Session completion response:', response.data);
      setCompletionData(response.data as CompletionResponse);
      setCompleted(true);

    } catch (error) {
      console.error('❌ Error completing collection:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to complete collection';
      setError(errorMessage);
    } finally {
      setCompleting(false);
    }
  };

  const handleFinish = () => {
    if (completionData) {
      const updatedSession: OnboardingSession = {
        ...session,
        status: completionData.summary.status,
        reservedItems: [], // Cleared since converted to assignments
        updatedAt: new Date().toISOString()
      };
      onComplete(updatedSession);
    }
    onClose();
  };

  // If completion is done, show results
  if (completed && completionData) {
    const { summary, completedAssignments, failedAssignments } = completionData;
    const issueDate = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit', 
      day: '2-digit'
    });

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden">
          
          {/* Success Header */}
          <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
                <div>
                  <h2 className="text-xl font-semibold text-green-900">
                    Equipment Collection Completed Successfully
                  </h2>
                  <p className="text-sm text-green-700">
                    Officer {session.officerName} - Badge #{session.badgeNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            
            {/* Summary Statistics */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-900">{summary.successful}</p>
                <p className="text-sm text-green-700">Items Assigned</p>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-900">{summary.failed}</p>
                <p className="text-sm text-red-700">Failed Assignments</p>
              </div>

              <div className={`${summary.status === 'Complete' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border rounded-lg p-4 text-center`}>
                {summary.status === 'Complete' ? (
                  <CheckSquare className="h-6 w-6 text-green-600 mx-auto mb-2" />
                ) : (
                  <Clock className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                )}
                <p className={`text-lg font-bold ${summary.status === 'Complete' ? 'text-green-900' : 'text-yellow-900'}`}>
                  {summary.status}
                </p>
                <p className={`text-sm ${summary.status === 'Complete' ? 'text-green-700' : 'text-yellow-700'}`}>
                  Session Status
                </p>
              </div>
            </div>

            {/* Successful Assignments */}
            {completedAssignments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                  Items Assigned ({completedAssignments.length})
                </h3>
                <div className="space-y-3">
                  {completedAssignments.map((assignment, index) => (
                    <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <Package className="h-5 w-5 text-green-600" />
                          <div>
                            <span className="font-medium text-green-900">{assignment.itemName}</span>
                            <div className="text-sm text-green-700 flex items-center space-x-4 mt-1">
                              <span className="flex items-center">
                                <Calendar className="h-4 w-4 mr-1" />
                                Issue Date: {issueDate}
                              </span>
                              <span className="flex items-center">
                                <User className="h-4 w-4 mr-1" />
                                Assignment ID: {assignment.assignmentId.substring(0, 12)}...
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="text-green-600">
                          <CheckCircle2 className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed Assignments */}
            {failedAssignments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
                  Failed Assignments ({failedAssignments.length})
                </h3>
                <div className="space-y-3">
                  {failedAssignments.map((failure, index) => (
                    <div key={index} className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <AlertCircle className="h-5 w-5 text-red-600" />
                          <div>
                            <span className="font-medium text-red-900">{failure.itemName}</span>
                            <p className="text-sm text-red-700 mt-1">{failure.error}</p>
                          </div>
                        </div>
                        <div className="text-red-600">
                          <AlertTriangle className="h-5 w-5" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Categories (Only show if session is Partial) */}
            {summary.status === 'Partial' && pendingCategories.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                  <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                  Categories Still Needed ({pendingCategories.length})
                </h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="space-y-2">
                    {pendingCategories.map((category, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-yellow-600" />
                          <span className="font-medium text-yellow-900">{category.categoryName}</span>
                        </div>
                        <div className="text-sm text-yellow-700">
                          Need {category.quantityNeeded} more item{category.quantityNeeded > 1 ? 's' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Next Steps */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-3 flex items-center">
                <ArrowRight className="h-5 w-5 text-blue-600 mr-2" />
                Next Steps
              </h4>
              <div className="space-y-2 text-sm text-blue-800">
                {summary.status === 'Complete' ? (
                  <>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      <span>All required equipment has been assigned to Officer {session.officerName}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      <span>Assignment records are now active and visible in the assignments dashboard</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      <span>Onboarding process is complete for this officer</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="h-4 w-4 text-blue-600" />
                      <span>Successfully assigned equipment is now active and tracked</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span>Pending categories will appear on the Quartermaster dashboard</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <ArrowRight className="h-4 w-4 text-blue-600" />
                      <span>Fulfill remaining categories individually when items become available</span>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button
                  onClick={handleFinish}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center space-x-2"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Finish</span>
                </button>
                
                {completedAssignments.length > 0 && (
                  <button
                    onClick={() => {
                      // TODO: Navigate to assignments view
                      console.log('Navigate to assignments view');
                    }}
                    className="px-4 py-2 text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 flex items-center space-x-2"
                  >
                    <ExternalLink className="h-4 w-4" />
                    <span>View Assignments</span>
                  </button>
                )}
              </div>

              <div className="text-sm text-gray-600">
                {summary.status === 'Complete' ? (
                  <span className="text-green-700 font-medium">✅ Onboarding Complete</span>
                ) : (
                  <span className="text-yellow-700 font-medium">⏳ Partial - {pendingCategories.length} categories pending</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial confirmation view
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              Complete Equipment Collection
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Officer {session.officerName} - Badge #{session.badgeNumber}
          </p>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          
          {/* Error Display */}
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                <span className="text-sm text-red-800">{error}</span>
              </div>
            </div>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span className="font-medium text-green-900">Ready to Assign</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{readyCount}</p>
              <p className="text-sm text-green-700">Items will become active assignments</p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 mb-2">
                <Clock className="h-5 w-5 text-yellow-600" />
                <span className="font-medium text-yellow-900">Pending Categories</span>
              </div>
              <p className="text-2xl font-bold text-yellow-900">{pendingCategories.length}</p>
              <p className="text-sm text-yellow-700">Categories need more items</p>
            </div>
          </div>

          {/* Ready Items Preview */}
          {reservedItems.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-2" />
                Items Ready for Assignment ({reservedItems.length})
              </h3>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {reservedItems.slice(0, 5).map((item, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-900">{item.itemName}</span>
                      {item.serialNumber && (
                        <span className="text-sm text-gray-600 ml-2">
                          Serial: {item.serialNumber}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-600">
                      Qty: {item.quantity || 1}
                    </div>
                  </div>
                ))}
                {reservedItems.length > 5 && (
                  <div className="text-sm text-gray-500 text-center py-2">
                    ... and {reservedItems.length - 5} more items
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Pending Categories Preview */}
          {pendingCategories.length > 0 && (
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Clock className="h-5 w-5 text-yellow-600 mr-2" />
                Categories Still Needed ({pendingCategories.length})
              </h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <div className="space-y-2">
                  {pendingCategories.map((category, index) => (
                    <div key={index} className="flex items-center justify-between text-sm">
                      <span className="font-medium text-yellow-900">{category.categoryName}</span>
                      <span className="text-yellow-700">Need {category.quantityNeeded} more</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Process Explanation */}
          <div className="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 className="font-medium text-blue-900 mb-2">What happens when you complete:</h4>
            <div className="space-y-2 text-sm text-blue-800">
              <div className="flex items-center space-x-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                <span>Ready items will be converted to active equipment assignments</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                <span>Each assignment gets today's date as the issue date</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                <span>Inventory quantities update automatically</span>
              </div>
              <div className="flex items-center space-x-2">
                <ArrowRight className="h-4 w-4 text-blue-600" />
                <span>Pending categories will be tracked for future fulfillment</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <button
              onClick={onClose}
              disabled={completing}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            
            <button
              onClick={handleCompleteCollection}
              disabled={completing || readyCount === 0}
              className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
            >
              {completing ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Processing Collection...</span>
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  <span>Complete Collection</span>
                </>
              )}
            </button>
          </div>
          
          {readyCount === 0 && (
            <p className="text-sm text-gray-500 mt-2">
              No items are ready for assignment. Please select equipment first.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompleteCollectionModal;